// Keeps the sidebar layout on this device and in sync with the server (/prefs/sidebar).
// Local-first: edits apply and persist immediately, then upload. When two devices
// disagree, the most recently edited layout wins. Tested in sync.test.ts.

import type { PlatformStorage } from '../platform'
import { emptyLayout, newer, normalize, type SidebarLayout } from './layout'

export type SyncStatus = 'local' | 'synced' | 'syncing' | 'offline'

interface Saved {
  layout: SidebarLayout
  /** Server version this copy is based on (0 = never synced). */
  version: number
}

export interface SyncOptions {
  storage: PlatformStorage
  /** Base URL of the prefs API (e.g. https://assembly.plato.so/prefs), or null for this-device-only. */
  prefsUrl: string | null
  token: string
  storageKey: string
  fetch?: typeof fetch
  debounceMs?: number
  onChange: (layout: SidebarLayout, status: SyncStatus) => void
}

export class SidebarSync {
  layout: SidebarLayout = emptyLayout()
  status: SyncStatus
  private version = 0
  /** The layout object last confirmed by the server; nothing to upload while unchanged. */
  private uploaded: SidebarLayout | null = null
  private timer: ReturnType<typeof setTimeout> | undefined
  private pushing: Promise<void> | null = null
  private closed = false
  private fetch: typeof fetch

  constructor(private opts: SyncOptions) {
    this.status = opts.prefsUrl ? 'syncing' : 'local'
    this.fetch = opts.fetch ?? ((...args) => fetch(...args))
  }

  private emit(status = this.status) {
    this.status = status
    if (!this.closed) this.opts.onChange(this.layout, status)
  }

  private async save() {
    const saved: Saved = { layout: this.layout, version: this.version }
    await this.opts.storage.set(this.opts.storageKey, JSON.stringify(saved)).catch(() => {})
  }

  /** Loads this device's copy, then reconciles with the server. */
  async start() {
    try {
      const raw = await this.opts.storage.get(this.opts.storageKey)
      if (raw) {
        const saved = JSON.parse(raw) as Partial<Saved>
        this.layout = normalize(saved.layout)
        this.version = typeof saved.version === 'number' ? saved.version : 0
      }
    } catch {
      /* corrupt local copy: start fresh, the server copy (if any) will win */
    }
    this.emit()
    await this.refresh()
  }

  close() {
    this.closed = true
    clearTimeout(this.timer)
  }

  /** Applies an edit locally right away and uploads it shortly after. */
  edit(fn: (layout: SidebarLayout) => SidebarLayout) {
    const next = fn(this.layout)
    if (next === this.layout) return
    this.layout = next
    this.save()
    if (!this.opts.prefsUrl) return this.emit('local')
    this.emit('syncing')
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.push(), this.opts.debounceMs ?? 600)
  }

  private headers() {
    return { Authorization: `Bearer ${this.opts.token}`, 'Content-Type': 'application/json' }
  }

  /** Pulls the server copy; adopts it if newer, uploads ours if ours is newer. */
  async refresh() {
    if (!this.opts.prefsUrl || this.closed) return
    try {
      const res = await this.fetch(`${this.opts.prefsUrl}/sidebar`, { headers: this.headers(), cache: 'no-store' })
      if (!res.ok) throw new Error(`prefs ${res.status}`)
      const body = (await res.json()) as { doc: unknown; version: number }
      const remote = body.doc ? normalize(body.doc) : null
      if (remote && remote.updatedAt > this.layout.updatedAt) {
        this.layout = remote
        this.uploaded = remote
        this.version = body.version
        await this.save()
        this.emit('synced')
      } else if (this.layout.updatedAt > (remote?.updatedAt ?? 0)) {
        this.version = body.version
        await this.push()
      } else {
        this.version = body.version
        if (remote) this.uploaded = this.layout
        await this.save()
        this.emit('synced')
      }
    } catch {
      this.emit('offline')
    }
  }

  /** Uploads the current layout, resolving version conflicts by last edit time. */
  push(): Promise<void> {
    this.pushing ??= this.runPush().finally(() => (this.pushing = null))
    return this.pushing
  }

  private async runPush() {
    if (!this.opts.prefsUrl || this.closed) return
    clearTimeout(this.timer)
    if (this.layout === this.uploaded) return this.emit('synced')
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const sent = this.layout
        const res = await this.fetch(`${this.opts.prefsUrl}/sidebar`, { method: 'PUT', headers: this.headers(), body: JSON.stringify({ doc: sent, baseVersion: this.version }) })
        if (res.ok) {
          this.version = ((await res.json()) as { version: number }).version
          this.uploaded = sent
          await this.save()
          // An edit made while uploading goes up next.
          if (this.layout !== sent) continue
          return this.emit('synced')
        }
        if (res.status !== 409) throw new Error(`prefs ${res.status}`)
        // Another device saved first.
        const current = (await res.json()) as { doc: unknown; version: number }
        this.version = current.version
        const remote = current.doc ? normalize(current.doc) : null
        const winner = newer(this.layout, remote)
        if (winner !== this.layout) {
          this.layout = winner
          this.uploaded = winner
          await this.save()
          return this.emit('synced')
        }
      }
      this.emit('offline')
    } catch {
      this.emit('offline')
    }
  }
}
